import { useQuery } from '@tanstack/react-query';
import { BRAND } from '@saesak/shared';
import { Card, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import type { SiteInfo } from '@/types';
import { SiteFooter } from './SiteFooter';

/**
 * 요금 안내 — 공개 페이지 (§5.21, §14.1-17).
 * 금액은 관리자 콘솔(정산 탭)의 요금제 마스터를 그대로 보여 준다 —
 * 가격 개정에 배포가 필요 없다. 상품·가격 표시(전자상거래·PG 심사 요건)도 겸한다.
 */
export function PricingPage() {
  const { data, isPending } = useQuery({
    queryKey: ['site-info'],
    queryFn: () => apiData<SiteInfo>('/site-info'),
    staleTime: 10 * 60_000,
  });

  const plans = (data?.plans ?? []).filter((plan) => plan.baseFee > 0);

  return (
    <main className="mx-auto w-full max-w-screen-sm px-6 py-10">
      <div className="mb-8 flex flex-col items-center gap-2 text-center">
        <img src="/brand-icon.svg" alt="" width={56} height={56} />
        <h1 className="text-2xl font-bold">요금 안내</h1>
        <p className="text-base text-ink-sub">
          {BRAND.name}는 기관(어린이집·유치원) 단위 연간 구독 서비스예요.
          <br />
          보호자와 교직원 개인에게는 비용을 받지 않습니다.
        </p>
      </div>

      {isPending && <Spinner />}

      <div className="flex flex-col gap-4">
        {plans.map((plan) => (
          <Card key={plan.code} className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-lg font-bold">{plan.name}</p>
              <p className="text-xl font-bold text-primary-strong">
                {plan.baseFee.toLocaleString('ko-KR')}원
                <span className="text-sm font-medium text-ink-sub"> /년 · VAT 별도</span>
              </p>
            </div>
            {plan.tiers.length > 0 && (
              <div className="rounded-sm bg-bg p-3 text-sm">
                <p className="mb-1.5 font-semibold text-ink-sub">원아 수에 따른 추가 금액 (연간)</p>
                {plan.tiers.map((tier, index) => (
                  <p key={index} className="flex justify-between">
                    <span>
                      원아 {tier.fromCount}
                      {tier.toCount ? `~${tier.toCount}명` : '명 이상'}
                    </span>
                    <span>+{tier.additionalFee.toLocaleString('ko-KR')}원</span>
                  </p>
                ))}
              </div>
            )}
            <ul className="flex flex-col gap-1 text-sm text-ink-sub">
              <li>· 알림장·앨범·출석·공지·쪽지 등 소통 기능 전체</li>
              <li>· 발달기록·새싹 나무·생활기록부 PDF 발급</li>
              <li>· 등하원 버스 실시간 위치, 전자 서류함, 운영 도구</li>
              <li>· 기관 구성원·보호자 계정 수 제한 없음</li>
            </ul>
          </Card>
        ))}

        {!isPending && plans.length === 0 && (
          <Card>
            <p className="font-semibold">요금 준비 중이에요</p>
            <p className="mt-1 text-sm text-ink-sub">
              도입 문의는 아래 연락처로 주시면 기관 규모에 맞는 견적서를 보내드립니다.
            </p>
          </Card>
        )}

        <Card className="bg-primary-soft">
          <p className="font-semibold text-primary-strong">이용·결제 안내</p>
          <ul className="mt-1.5 flex flex-col gap-1 text-sm">
            <li>· 표시된 모든 금액은 부가가치세(VAT) 별도이며, 결제 시 부가세가 더해집니다.</li>
            <li>· 신규 기관에는 무료 체험 기간이 제공됩니다.</li>
            <li>· 결제는 견적서에 따른 계좌이체(무통장 입금)이며, 입금 확인 후 거래명세서가 발행됩니다.</li>
            <li>
              · 환불 규정은{' '}
              <a href="/terms#refund" className="underline">
                이용약관 제8조
              </a>
              를 따릅니다 — 개시 전 전액 환불, 개시 후 잔여 기간 환불.
            </li>
          </ul>
        </Card>
      </div>

      <SiteFooter />
    </main>
  );
}
